import type { PatientVital } from '@/types/vitals-api';
import { VITAL_CODES } from '@/types/vitals-api';

/**
 * Client-side derived-vital computation. Mirrors Flutter `vital_data.dart:bmi(list)`
 * + `waistToHipRatio(list)` and the underlying `calculateBmi` /
 * `calculateWaistToHipRatio` in `scrips_core/lib/utils/utils.dart:554-575`. Ported
 * from the PM app (dev-scrips-pm-react/.../vitals/vital-derive.ts).
 *
 * Used by the vitals-history drawer (PROD-1535) to mirror the Flutter Historical
 * matrix, which displays a BMI column computed client-side from each encounter's
 * height + weight — the server doesn't persist BMI as a separate row.
 *
 * Formulas (verbatim from Flutter):
 *   BMI = round((weight * 10000) / (height * height))   (weight kg, height cm)
 *   W/H = waist / hip                                    (full decimal)
 *
 * INTENTIONAL Flutter divergence at the zero-value edge: Flutter's `calculateBmi`
 * returns 0 when either input is 0 (renders "BMI = 0"); React returns null so the
 * cell renders "—". The MISSING-input path matches Flutter exactly (null → no row).
 */

function parseNumeric(value: string | null | undefined): number | null {
  if (value == null || value.trim() === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function findReadingValue(readings: PatientVital[], code: string): number | null {
  const reading = readings.find((r) => r.code?.code === code);
  return parseNumeric(reading?.quantity?.value);
}

/**
 * Compute BMI from height + weight readings. Returns null if either input is missing
 * or non-positive. Result is a whole number (Flutter `.roundToDouble()`).
 */
/**
 * Display-sanity bounds for CLIENT-DERIVED values (Samer vitals review 2026-07-23:
 * a derived "BMI 120" — from height 90cm / weight 97kg garbage inputs — rendered
 * uncritically). A derived number WE computed from implausible inputs is our own
 * fabrication; outside these bounds we suppress the synthetic row rather than
 * publish it. SERVER-persisted rows are untouched (source-of-truth rule), and these
 * are arithmetic-sanity bounds, not clinical thresholds (never recomputed client-side).
 */
export const BMI_DISPLAY_BOUNDS = { min: 8, max: 100 } as const;
export const WHR_DISPLAY_BOUNDS = { min: 0.3, max: 2 } as const;

export function computeBmi(readings: PatientVital[]): number | null {
  return bmiFromWeightHeight(
    findReadingValue(readings, VITAL_CODES.WEIGHT),
    findReadingValue(readings, VITAL_CODES.HEIGHT),
  );
}

/** The same BMI rule on plain numbers (kg, cm), for surfaces whose rows are not `PatientVital`. */
export function bmiFromWeightHeight(weight: number | null, height: number | null): number | null {
  if (!weight || !height) return null;
  const bmi = Math.round((weight * 10000) / (height * height));
  if (bmi < BMI_DISPLAY_BOUNDS.min || bmi > BMI_DISPLAY_BOUNDS.max) return null;
  return bmi;
}

/**
 * Compute waist-to-hip ratio. Server typically returns this already; this helper is
 * the fallback when it's missing — matches Flutter parity for the History view.
 */
export function computeWaistToHipRatio(readings: PatientVital[]): number | null {
  const waist = findReadingValue(readings, VITAL_CODES.WAIST);
  const hip = findReadingValue(readings, VITAL_CODES.HIP);
  if (!waist || !hip) return null;
  const whr = waist / hip;
  if (whr < WHR_DISPLAY_BOUNDS.min || whr > WHR_DISPLAY_BOUNDS.max) return null;
  return whr;
}

/**
 * Returns a new PatientVital array with synthetic BMI and Waist-to-Hip Ratio rows
 * injected from client-side computation. Existing rows are left untouched; we only
 * synthesize when the server didn't provide a value.
 */
export function injectDerivedReadings(readings: PatientVital[]): PatientVital[] {
  const result = [...readings];

  const existingBmi = result.find((r) => r.code?.code === VITAL_CODES.BMI);
  const hasBmiValue =
    !!existingBmi?.quantity?.value && existingBmi.quantity.value.trim() !== '';
  if (!hasBmiValue) {
    const bmi = computeBmi(readings);
    if (bmi != null) {
      result.push(makeSynthetic(VITAL_CODES.BMI, String(bmi)));
    }
  }

  const existingWhr = result.find(
    (r) => r.code?.code === VITAL_CODES.WAIST_TO_HIP_RATIO,
  );
  const hasWhrValue =
    !!existingWhr?.quantity?.value && existingWhr.quantity.value.trim() !== '';
  if (!hasWhrValue) {
    const whr = computeWaistToHipRatio(readings);
    if (whr != null) {
      // Format to 2 decimals to keep the display tidy (Flutter renders the raw double).
      result.push(makeSynthetic(VITAL_CODES.WAIST_TO_HIP_RATIO, whr.toFixed(2)));
    }
  }

  return result;
}

function makeSynthetic(code: string, value: string): PatientVital {
  // Mark as `derived-<code>` identifire so React keys + dev inspection make it obvious
  // this row was computed client-side rather than returned by the server.
  return {
    identifire: `derived-${code}`,
    status: null,
    code: {
      code,
      Definition: null,
      display: null,
      system: null,
      description: null,
      shortName: null,
    },
    quantity: { value, unit: null, system: null, code },
    subject: null,
    encounter: null,
    category: null,
    dataAbsentReason: null,
    deleteProvider: null,
    effectiveDateTime: null,
    vitalNormalStatus: null,
    vitalAbnormalPercentage: null,
    lmp: null,
    hcm: null,
    isDeleted: false,
    createdDate: null,
    isMenstrual: null,
    isBreastFeeding: null,
    extraData: null,
  };
}
